import React, { useEffect, useState } from "react";

const NODES = [
  { id: "home", label: "Spark" },
  { id: "thesis-editorial-section", label: "Seed" },
  { id: "venture-studio-section", label: "Sprout" },
  { id: "portfolio-section", label: "Scale" },
  { id: "final-cta-section", label: "Bloom" },
];

export default function GrowthStem() {
  const [pct, setPct] = useState(0);
  const [nodes, setNodes] = useState(NODES.map(() => ({ y: 0, active: false })));

  useEffect(() => {
    const measure = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      const scrollY = window.scrollY;
      const p = total > 0 ? Math.min(1, Math.max(0, scrollY / total)) : 0;
      setPct(p * 100);

      const reached = NODES.map((n) => {
        const el =
          document.getElementById(n.id) ||
          document.querySelector(`[data-testid="${n.id}"]`);
        if (!el) return { y: 0, active: false };
        const rect = el.getBoundingClientRect();
        const docTop = rect.top + window.scrollY;
        // Map docTop into 0..1 along the page scroll
        const y = total > 0 ? Math.min(1, Math.max(0, docTop / (total + window.innerHeight))) : 0;
        const active = window.scrollY + window.innerHeight * 0.55 >= docTop;
        return { y: y * 100, active };
      });
      setNodes(reached);
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="growth-stem"
      className="hidden lg:block fixed left-6 top-0 bottom-0 z-[45] pointer-events-none"
      style={{ width: 24 }}
    >
      {/* Stem track (ghost) */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-24 bottom-16 w-[2px] rounded-full"
        style={{ background: "rgba(18, 71, 37, 0.12)" }}
      />
      {/* Stem fill */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-24 w-[2px] rounded-full"
        style={{
          height: `calc((100% - 10rem) * ${pct / 100})`,
          background: "linear-gradient(180deg, #E8A317 0%, #AECE4F 50%, #124725 100%)",
          boxShadow: "0 0 8px rgba(174, 206, 79, 0.5)",
          transition: "height 120ms linear",
        }}
      />

      {/* Stage nodes (leaves bloom when reached) */}
      {nodes.map((n, i) => {
        const node = NODES[i];
        // Position along the stem visible region (top-24 to bottom-16)
        const top = `calc(6rem + (100% - 10rem) * ${n.y / 100})`;
        const blooming = n.active;
        return (
          <div
            key={node.id}
            className="absolute left-1/2 -translate-x-1/2"
            style={{ top, transition: "top 200ms ease-out" }}
          >
            {/* Leaf */}
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              className="block"
              style={{
                transform: `scale(${blooming ? 1 : 0.45}) rotate(${blooming ? (i % 2 === 0 ? -28 : 28) : 0}deg)`,
                opacity: blooming ? 1 : 0.4,
                transition: "transform 600ms cubic-bezier(0.22,1,0.36,1), opacity 400ms ease",
                filter: blooming
                  ? "drop-shadow(0 0 6px rgba(174,206,79,0.65))"
                  : "none",
              }}
            >
              <path
                d="M11 1 C 5 4, 2 10, 4 18 C 12 16, 18 11, 18 4 C 16 3, 13 1.5, 11 1 Z"
                fill={blooming ? "#AECE4F" : "rgba(18,71,37,0.25)"}
                stroke={blooming ? "#124725" : "transparent"}
                strokeWidth="0.6"
              />
              <path
                d="M11 1 L 8 17"
                stroke={blooming ? "#124725" : "rgba(18,71,37,0.3)"}
                strokeWidth="0.6"
                fill="none"
              />
            </svg>
            {/* Label */}
            <span
              className={`absolute left-7 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] uppercase tracking-[0.28em] font-bold font-mono-data transition-all ${
                blooming
                  ? "text-[#124725] opacity-95 translate-x-0"
                  : "text-[#124725]/40 opacity-0 -translate-x-1"
              }`}
            >
              {String(i + 1).padStart(2, "0")} · {node.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
